import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput, { parseFractionLikeInput as parseFraction } from "../../../../components/inputs/FractionInput";
import CirclePolygonAnglesDiagram from "../../../../components/diagrams/CirclePolygonAnglesDiagram";

const DEC_TOL = 0.1;
const GIVEN_ANGLES = [70, 75, 80, 85, 95, 100, 105, 110];

function rawText(value) {
  if (value == null) return "";
  if (typeof value === "string" || typeof value === "number") return String(value);
  return String(value.raw ?? value.value ?? value.input ?? "");
}

function normalize(raw) {
  return String(raw ?? "").trim().replace(/\u2212/g, "-").replace(/\s+/g, "").replace(",", ".");
}

function pretty(raw) {
  return String(raw ?? "").trim().replace(".", "{,}");
}

export function generateTask() {
  const given = GIVEN_ANGLES[Math.floor(Math.random() * GIVEN_ANGLES.length)];
  const givenVertex = Math.random() < 0.5 ? "A" : "B";
  return {
    type: "FindOppositeAngleInCyclicQuadrilateralTask",
    data: {
      given,
      givenVertex,
      unknownVertex: givenVertex === "A" ? "C" : "D",
      answer: 180 - given,
    },
  };
}

export function renderTask({ data }) {
  const { given, givenVertex, unknownVertex } = data;
  return (
    <div style={{ marginTop: 8, marginBottom: 8 }}>
      {"Czworok\u0105t "}<InlineMath math="ABCD" />{" jest wpisany w okr\u0105g. "}
      {"K\u0105t "}<InlineMath math={`\\angle ${givenVertex}`} />{" ma miar\u0119 "}<InlineMath math={`${given}^{\\circ}`} />{". "}
          {"Oblicz miar\u0119 k\u0105ta "}<span style={{ whiteSpace: "nowrap" }}><InlineMath math={"\\alpha"} />{" w wierzcho\u0142ku "}<InlineMath math={unknownVertex} />{"."}</span>
      <div style={{ marginTop: 12, display: "flex", justifyContent: "center" }}>
        <CirclePolygonAnglesDiagram scenario="cyclicQuadrilateral" givenDeg={{ value: given, vertex: givenVertex }} />
      </div>
    </div>
  );
}

export function renderInput(_task, userAnswer, onChange, disabled) {
  return (
    <FractionInput
      value={rawText(userAnswer)}
      onChange={(value) => onChange({ raw: rawText(value) })}
      disabled={disabled}
          labelLatex={"\\alpha"}
          suffixLatex={"^\\circ"}
      placeholder="np. 80"
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  const raw = rawText(userAnswer);
  if (!raw.trim()) return false;
  const value = parseFraction(normalize(raw));
  return Number.isFinite(value) && Math.abs(value - data.answer) <= DEC_TOL;
}

export function renderAnswer({ data }) {
  return <InlineMath math={`\\alpha=${data.answer}^{\\circ}`} />;
}

export function renderExplanation({ data }) {
  const { given, givenVertex, unknownVertex, answer } = data;
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <strong>Krok 1.</strong> {"W czworok\u0105cie wpisanym w okr\u0105g suma miar k\u0105t\u00f3w przeciwleg\u0142ych jest r\u00f3wna "}<InlineMath math="180^{\\circ}" />.
      <BlockMath math={`\\angle ${givenVertex}+\\angle ${unknownVertex}=180^{\\circ}`} />
      <strong>Krok 2.</strong> {"Obliczamy brakuj\u0105cy k\u0105t."}
      <BlockMath math={`\\angle ${unknownVertex}=180^{\\circ}-${given}^{\\circ}=${answer}^{\\circ}`} />
      <div style={{ marginTop: 10 }}>
        {"Odpowied\u017a: "}<InlineMath math={`\\alpha=\\angle ${unknownVertex}=${answer}^{\\circ}`} />.
      </div>
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  const raw = rawText(userAnswer);
  if (!raw.trim()) return <i>(brak odpowiedzi)</i>;
  return <InlineMath math={`\\alpha=${pretty(raw)}^{\\circ}`} />;
}

const taskModule = {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderExplanation,
  renderUserAnswer,
};

export default taskModule;
